/**
 * Academy domain — `AcademyContentStore` state shape. Remote-only, in-memory.
 * Caches chapters, caption languages, WebVTT caption text and transcript-search
 * matches for both live recordings and course items. Cache keys are the `eventId`
 * for live recordings and `${courseId}:${itemId}` for course items.
 */
import type {
  ChapterResponse,
  TranscriptCueResponse,
} from './academy.model';
import type { TranscriptMatch } from './academy-responses.model';

export interface AcademyContentState {
  /** Chapters, keyed by `eventId` or `${courseId}:${itemId}`. */
  chaptersByKey: Record<string, ChapterResponse[]>;
  /** Available caption language codes, keyed by `eventId` or `${courseId}:${itemId}`. */
  captionLanguagesByKey: Record<string, string[]>;
  /** WebVTT caption text, keyed `${target}#${lang}` where target is the content key. */
  captionTextByKey: Record<string, string>;
  /** Transcript-search matches, keyed `${target}#${q}#${lang}` (lang empty when omitted). */
  transcriptMatchesByKey: Record<string, TranscriptMatch[]>;
  /** Producer-authored chapters per `LiveEventId` (OData authoring view, not the gated read). */
  authoredChaptersByEvent: Record<string, ChapterResponse[]>;
  /** Producer-authored transcript cues per `LiveEventId`. */
  authoredCuesByEvent: Record<string, TranscriptCueResponse[]>;
}

export const initialAcademyContentState: AcademyContentState = {
  chaptersByKey: {},
  captionLanguagesByKey: {},
  captionTextByKey: {},
  transcriptMatchesByKey: {},
  authoredChaptersByEvent: {},
  authoredCuesByEvent: {},
};
